import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LayoutDashboard, Package, User, LogOut, Menu, X, ChevronRight, FileText, MessageCircle, ClipboardList } from 'lucide-react';
import { Button } from '../ui/button';

const NAV_ITEMS = [
    { path: '/fournisseur', icon: LayoutDashboard, label: 'Tableau de bord' },
    { path: '/fournisseur/produits', icon: Package, label: 'Mes Produits' },
    { path: '/fournisseur/commandes', icon: FileText, label: 'Commandes' },
    { path: '/fournisseur/bons-livraison', icon: ClipboardList, label: 'Bons de Livraison' },
    { path: '/fournisseur/messages', icon: MessageCircle, label: 'Messages' },
    { path: '/fournisseur/profil', icon: User, label: 'Mon Profil' },
];

export const SupplierLayout = ({ children }) => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/connexion');
    };

    return (
        <div className="min-h-screen bg-gray-50 flex" data-testid="supplier-layout">
            {/* Sidebar */}
            <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform lg:translate-x-0 lg:static ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="h-full flex flex-col">
                    {/* Logo */}
                    <div className="p-4 border-b flex items-center justify-between">
                        <div>
                            <h1 className="text-lg font-bold text-emerald-700">PRIXDEAL</h1>
                            <p className="text-xs text-gray-500">Espace Fournisseur</p>
                        </div>
                        <Button variant="ghost" size="sm" className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                            <X className="w-4 h-4" />
                        </Button>
                    </div>

                    {/* Supplier info */}
                    <div className="p-4 border-b bg-emerald-50">
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                                <p className="font-semibold text-sm text-emerald-800 truncate">{user?.company_name}</p>
                                <p className="text-xs text-emerald-600 font-mono">Code: {user?.code}</p>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleLogout}
                                className="text-red-600 hover:bg-red-50 h-8 px-2 shrink-0"
                                title="Déconnexion"
                                data-testid="supplier-quick-logout-btn"
                            >
                                <LogOut className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-3 space-y-1">
                        {NAV_ITEMS.map(item => {
                            const isActive = location.pathname === item.path || 
                                (item.path !== '/fournisseur' && location.pathname.startsWith(item.path));
                            return (
                                <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? 'bg-emerald-100 text-emerald-800 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}>
                                    <item.icon className="w-4 h-4" />
                                    {item.label}
                                    {isActive && <ChevronRight className="w-3 h-3 ml-auto" />}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* Logout */}
                    <div className="p-3 border-t">
                        <button onClick={handleLogout}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-600 hover:bg-red-50 w-full transition-colors">
                            <LogOut className="w-4 h-4" />
                            Déconnexion
                        </button>
                    </div>
                </div>
            </aside>

            {/* Overlay mobile */}
            {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

            {/* Main */}
            <main className="flex-1 min-h-screen">
                <header className="bg-white border-b px-4 py-3 flex items-center gap-3 lg:hidden">
                    <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(true)} data-testid="supplier-mobile-menu-btn">
                        <Menu className="w-5 h-5" />
                    </Button>
                    <span className="font-semibold text-sm flex-1 truncate">{user?.company_name}</span>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleLogout}
                        className="text-red-600 hover:bg-red-50"
                        data-testid="supplier-mobile-logout-btn"
                        title="Déconnexion"
                    >
                        <LogOut className="w-5 h-5" />
                    </Button>
                </header>
                <div className="p-4 lg:p-6 max-w-7xl mx-auto">
                    {children}
                </div>
            </main>
        </div>
    );
};
